import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import classNames from 'classnames';

import {
    useKeyboard,
} from '~Betslip/store/keyboardContext';

import {
    selectAvailableFreebets,
} from '~Betslip/selectors/freeBetSelectors';

import {
    selectAvailableBetTypes,
} from '~Betslip/selectors/availableBetTypesSelectors';

import formatCurrency, {
    deLocalizeNumberString,
} from '~shared/utils/currencyUtils';

import {
    getEachwayAllowed,
} from '~shared/utils/user';

// no more than 7 integer-digits and 2 decimal-digits per stake
const decimalRegex = /^(0|[1-9][0-9]{0,6})((\.|,)[0-9]{0,2})?$/g;
const storeDecimalSeparator = '.';

import './styles.scss';
import { isUserLoggedIn } from '../../../../../../../shared/utils/user';

const stakeDecimalFormat = (value) => formatCurrency(
    value,
    undefined, // no currency
    undefined, // no locale
    false, // no currency symbol
    false, // no thousand separator
);

const getValueToDisplay = (stake, freebet, each_way_selected) => {
    const ewFreebetDivider = each_way_selected ? 2 : 1;
    const freebetValue = freebet ? freebet.value / ewFreebetDivider : 0;
    const stakeValue = freebet ? stakeDecimalFormat(freebetValue) : stake;

    return stakeValue;
};

const EditableStakeValue = ({
    stake,
    stakeClasses,
    localStakeFieldRef,
    onChangeCallback,
    onExitEditMode,
    each_way_selected,
    freebet,
    gtmAttribute,
}) => {
    const { closeKeyboard } = useKeyboard();

    const availableBetTypes = useSelector(selectAvailableBetTypes);
    useEffect(() => {
        return () => {
            if (typeof onExitEditMode === 'function') {
                onExitEditMode();
            }
        };
    }, []);


    const firstUpdate = useRef(true);
    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;

            return;
        }
        closeKeyboard();
    }, [availableBetTypes]);

    const onKeyPress = (e) => {
        if (e.key === 'Enter') {
            closeKeyboard();
        }
    };

    const stakeToDisplay = stake || '';
    const stakeValue = getValueToDisplay(stakeToDisplay, freebet, each_way_selected);

    return (
        <input
            type="text"
            autoFocus
            className={stakeClasses}
            value={stakeValue}
            ref={localStakeFieldRef}
            onChange={onChangeCallback}
            onKeyPress={onKeyPress}
            inputMode="none"
            data-spectate-product={gtmAttribute}
        />
    );
};

EditableStakeValue.propTypes = {
    stake: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    stakeClasses: PropTypes.string,
    localStakeFieldRef: PropTypes.object,
    onChangeCallback: PropTypes.func,
    onExitEditMode: PropTypes.func,
    each_way_selected: PropTypes.bool,
    freebet: PropTypes.object,
    hasFreebetOptions: PropTypes.bool,
    gtmAttribute: PropTypes.string,
};

const ViewableStakeValue = ({
    stake,
    stakeClasses,
    onSelectCallback,
    disabled,
    freebet,
    each_way_selected,
}) => {
    const placeholder = stakeDecimalFormat(0);

    // if stake is an integer 0, we want it to be passed as '' into the input
    const stakeToDisplay = stake ? stakeDecimalFormat(stake) : '';
    const stakeValue = getValueToDisplay(stakeToDisplay, freebet, each_way_selected);

    const viewableStakeClasses = classNames(stakeClasses, 'fullslip__stake-value--viewable');

    return (
        <input
            type="text"
            placeholder={placeholder}
            className={viewableStakeClasses}
            value={stakeValue}
            readOnly
            onSelect={onSelectCallback}
            disabled={disabled}
            data-test-id="betslip;stake"
        />
    );
};

ViewableStakeValue.propTypes = {
    stake: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    stakeClasses: PropTypes.string,
    onSelectCallback: PropTypes.func,
    disabled: PropTypes.bool,
    freebet: PropTypes.object,
    each_way_selected: PropTypes.bool,
    hasFreebetOptions: PropTypes.bool,
};

ViewableStakeValue.defaultProps = {
    disabled: false,
};

const StakeField = ({
    stake,
    each_way_allowed,
    each_way_selected,
    onChangeCallback,
    onClickHandler,
    disabled,
    onAcceptHandler,
    freebetId,
    betslipId,
    gtmDetails,
    depositRequired,
}) => {
    const [ t ] = useTranslation();
    const localStakeFieldRef = useRef();
    const { setStakeFieldRef, stakeFieldRef, setFreebets, selectedFreebet, setSelectedFreebet } = useKeyboard();
    const containerClasses = classNames(
        'fullslip__stake-container',
        {
            'fullslip__stake-container--disabled': disabled,
        },
    );

    const freebets = useSelector((state) => selectAvailableFreebets(state, betslipId, freebetId));
    const hasFreebetOptions = freebets.length > 0;
    const appliedFreebet = freebets.find(freebet => freebet.id === freebetId);
    let currentFreebetId = localStakeFieldRef?.current?.freebetId || freebetId || null;
    const [prevFreebetId, setPrevFreebetId] = useState(freebetId || null);
    const selectedFreebetId = selectedFreebet?.id || null;
    const isDepositRequired = isUserLoggedIn() ? depositRequired : false;

    const stakeFieldClasses = classNames(
        'fullslip__stake-field',
        {
            'fullslip__stake-field--freebet': appliedFreebet,
            'fullslip__stake-field--freebet-available': hasFreebetOptions,
            'fullslip__stake-field--deposit-required': isDepositRequired,
        },
    );

    useEffect(() => {
        if (prevFreebetId !== currentFreebetId &&
            currentFreebetId === selectedFreebetId) {
            onChangeCallback(stake, each_way_selected, selectedFreebetId);
            setPrevFreebetId(selectedFreebetId);
        }
    }, [selectedFreebet]);

    const eachWayCheckbox = () => {
        if (!each_way_allowed || !getEachwayAllowed()) {
            return null;
        }

        const gtmEnd = each_way_selected ? 'disabled' : 'enabled';
        const betTypeGTM = isNaN(Number(gtmDetails.betType)) ? `${gtmDetails.betType};` : '';
        const gtmAttribute = `${gtmDetails.source};${betTypeGTM}each-way-${gtmEnd}`;

        return (
            <div className="fullslip__ew-checkbox">
                <label className="fullslip__ew-label">{t('betslip.ew')}</label>
                <input
                    className="fullslip__ew-input"
                    data-test-id="betslip;ew-checkbox"
                    data-spectate-product={gtmAttribute}
                    type="checkbox"
                    checked={each_way_selected}
                    onChange={() => onChangeCallback(stake, !each_way_selected, currentFreebetId)}
                />
            </div>
        );
    };

    const isStakeValueValid = (val) => val === '' || String(val).match(decimalRegex);

    // controls both custom and physical keyboards input
    const handleStakeValueChange = (e) => {
        let valueWithoutFreebet = e.target.value;
        let stakeFreebetId = selectedFreebetId;
        if (selectedFreebet) {
            let freebetValue = selectedFreebet.value;
            if (each_way_selected) {
                freebetValue = freebetValue / 2;
            }
            valueWithoutFreebet = valueWithoutFreebet.replace(freebetValue, '');
            setSelectedFreebet(null);
            stakeFreebetId = null;
        }
        let stakeValue = deLocalizeNumberString(valueWithoutFreebet);

        // prevent from sending stake update like "05"
        if (stake === '0' && stakeValue && stakeValue[1] !== storeDecimalSeparator) {
            stakeValue = stakeValue[1];
        }

        // add leading zero if customer typed in decimal separator
        if (stakeValue === storeDecimalSeparator) {
            stakeValue = `0${storeDecimalSeparator}`;
        }

        if (!isStakeValueValid(stakeValue)) {
            return;
        }

        onChangeCallback(stakeValue, each_way_selected, stakeFreebetId);
    };

    const openKeyboard = () => {
        setStakeFieldRef(localStakeFieldRef);
        onClickHandler();
        setSelectedFreebet(appliedFreebet);
        setFreebets(freebets);
    };

    const isEditMode = stakeFieldRef === localStakeFieldRef;
    const stakeClasses = classNames('fullslip__stake-value',
        {
            'fullslip__stake-value--freebet': hasFreebetOptions,
        },
    );

    const gtmAttribute = `${gtmDetails.source};stake`;

    return (
        <div className={containerClasses}>
            <div
                className={stakeFieldClasses}
                onClick={openKeyboard}
                data-spectate-product={gtmAttribute}
            >
                {
                    isEditMode
                    ?
                        <EditableStakeValue
                            stake={stake}
                            stakeClasses={stakeClasses}
                            localStakeFieldRef={localStakeFieldRef}
                            onChangeCallback={handleStakeValueChange}
                            each_way_selected={each_way_selected}
                            freebet={appliedFreebet}
                            onExitEditMode={onAcceptHandler}
                            hasFreebetOptions={hasFreebetOptions}
                            gtmAttribute={gtmAttribute}
                        />
                    :
                        <ViewableStakeValue
                            stake={stake}
                            stakeClasses={stakeClasses}
                            onSelectCallback={openKeyboard}
                            disabled={disabled}
                            freebet={appliedFreebet}
                            each_way_selected={each_way_selected}
                            hasFreebetOptions={hasFreebetOptions}
                        />
                }
                {
                    appliedFreebet
                    ? <div className="fullslip__stake-text fullslip__stake-text--freebet">{t('betslip.freebet')}</div>
                    : <div className="fullslip__stake-text">{t('betslip.stake')}</div>
                }
            </div>
            {eachWayCheckbox()}
        </div>
    );
};

StakeField.propTypes = {
    stake: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    each_way_selected: PropTypes.bool,
    each_way_allowed: PropTypes.bool,
    onChangeCallback: PropTypes.func,
    onClickHandler: PropTypes.func,
    disabled: PropTypes.bool,
    onAcceptHandler: PropTypes.func,
    freebetId: PropTypes.number,
    betslipId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    gtmDetails: PropTypes.object,
    depositRequired: PropTypes.bool,
};

StakeField.defaultProps = {
    disabled: false,
};

export default StakeField;
